import { useState } from "react";
import { Loader2, Settings } from "lucide-react";
import { toast } from "sonner";
import { changeName, changePassword, changeEmail, errorDetail } from "@/lib/reader";

const inputCls = "w-full bg-[#0B0C10] border border-white/15 px-4 py-3 text-sm placeholder:text-white/30 focus:outline-none focus:border-primary-gold";

function Card({ title, testId, onSubmit, busy, children, cta }) {
  return (
    <form onSubmit={onSubmit} className="border border-white/10 bg-[#121212] p-6 flex flex-col gap-3" data-testid={testId}>
      <h3 className="font-mono-ui text-[10px] uppercase tracking-[0.22em] text-primary-gold">{title}</h3>
      {children}
      <button type="submit" disabled={busy} data-testid={`${testId}-submit`} className="mt-auto btn-ghost px-4 py-3 font-mono-ui text-xs uppercase tracking-[0.18em] flex items-center justify-center gap-2 disabled:opacity-50">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} {cta}
      </button>
    </form>
  );
}

function useAction(fn, onDone) {
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onDone(await fn());
    } catch (err) {
      toast.error(errorDetail(err));
    } finally {
      setBusy(false);
    }
  };
  return [busy, run];
}

export default function ProfileSettings({ user, onChanged }) {
  const [name, setName] = useState(user.name || "");
  const [pw, setPw] = useState({ current: "", next: "" });
  const [em, setEm] = useState({ email: "", password: "" });

  const [nameBusy, saveName] = useAction(() => changeName(name), () => { toast.success("Name updated."); onChanged(); });
  const [pwBusy, savePw] = useAction(
    () => changePassword({ current_password: pw.current || null, new_password: pw.next }),
    () => { toast.success(user.has_password ? "Password changed." : "Password set — you can now sign in with email too."); setPw({ current: "", next: "" }); onChanged(); }
  );
  const [emBusy, saveEm] = useAction(
    () => changeEmail({ new_email: em.email, current_password: em.password }),
    (r) => { toast.success(`Check ${r.pending_email} — click the link we sent to confirm the change.`, { duration: 8000 }); setEm({ email: "", password: "" }); }
  );

  return (
    <section className="mt-12" data-testid="account-profile">
      <h2 className="flex items-center gap-3 font-serif-display text-3xl"><Settings className="w-6 h-6 text-primary-gold" /> Profile & security</h2>
      <div className="grid md:grid-cols-3 gap-4 mt-6">
        <Card title="Your name" testId="profile-name-form" onSubmit={saveName} busy={nameBusy} cta="Save name">
          <input required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="profile-name-input" />
        </Card>
        <Card title={user.has_password ? "Change password" : "Set a password"} testId="profile-password-form" onSubmit={savePw} busy={pwBusy} cta={user.has_password ? "Change password" : "Set password"}>
          {user.has_password && (
            <input required type="password" placeholder="Current password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className={inputCls} data-testid="profile-current-password" />
          )}
          <input required type="password" minLength={8} placeholder="New password (8+ characters)" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className={inputCls} data-testid="profile-new-password" />
        </Card>
        <Card title="Change email" testId="profile-email-form" onSubmit={saveEm} busy={emBusy} cta="Change email">
          {user.has_password ? (
            <>
              <input required type="email" placeholder="New email address" value={em.email} onChange={(e) => setEm({ ...em, email: e.target.value })} className={inputCls} data-testid="profile-new-email" />
              <input required type="password" placeholder="Current password" value={em.password} onChange={(e) => setEm({ ...em, password: e.target.value })} className={inputCls} data-testid="profile-email-password" />
            </>
          ) : (
            <p className="text-soft text-sm" data-testid="profile-email-needs-password">Set a password first, then you can change your email here.</p>
          )}
        </Card>
      </div>
      <p className="text-soft text-xs mt-3">Email changes take effect once you click the link we send to the new address. We email you whenever your password or email changes.</p>
    </section>
  );
}
